import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { DefaultFetchService } from "app/services/fetchService";

import type { IFoldersAPIResponse, IFoldersFilters, IFoldersTransformedData } from "../../../types";
import { transformFoldersData, searchNestedData } from "../utils/dataTransformers";

const SERVER_API = "widgetApi/LeadingFolders/LeadingFolders/Table";
const fetchService = DefaultFetchService.getInstance();

const fetchFoldersTable = async (filters: Record<string, any>) => {
    try {
        return await fetchService.get<IFoldersAPIResponse>(SERVER_API, filters);
    } catch (error) {
        console.error("Failed to fetch folders data:", error);
        throw error;
    }
};

export const useFoldersData = (
    filters: IFoldersFilters,
    searchTerm: string,
): {
    transformedData: IFoldersTransformedData;
    isTableLoading: boolean;
    error: any;
} => {
    const {
        data: tableData,
        isLoading: isTableLoading,
        error,
    } = useQuery({
        queryKey: ["folders-table", filters],
        queryFn: () => fetchFoldersTable(filters),
        enabled: !!filters.keys,
    });

    const transformedData = useMemo(() => {
        const baseData = transformFoldersData(tableData, searchTerm, filters.isTrending);
        if (!searchTerm?.trim()) {
            return baseData;
        }
        const filteredData = searchNestedData(baseData.data, searchTerm);
        return {
            data: filteredData,
            totalCount: baseData.totalCount,
            totalTopLevelCount: baseData.totalTopLevelCount,
        };
    }, [tableData, searchTerm, filters.isTrending]);

    return {
        transformedData: transformedData || { data: [], totalCount: 0, totalTopLevelCount: 0 },
        isTableLoading,
        error,
    };
};
